<template>
  <div>
    <h3>{{$t('message.init_mode') | title}}</h3>

    <b-form v-if="initConfig != null && networkConfig != null">
      <b-alert variant="info" :show="initConfig.state=='start'">
        {{$t('message.init_hint_network_first')}}
      </b-alert>
      <div>
        <b-form-select id="mode-select" v-model="initConfig.mode"
          :options="modeOptions" :disabled="readOnly"/>
      </div>
      <div v-if="initConfig.mode === 'cluster' && !readOnly" class="form-group form-check">
        <input type="checkbox" class="form-check-input" @change="onNewClusterChanged" id="show_cluster_nodes"/>
          <label for="show_cluster_nodes">
            {{$t('message.button_new_cluster') | title}}
          </label>
      </div>

      <b-form-row v-if="initConfig.mode !== 'cluster'">
        <div class="col-4">
          <span class="label label-default">
            {{$t('message.hostname') | title}}
          </span>
        </div>
        <div class="col-4">
          <b-form-input v-model="hostname"/>
        </div>
      </b-form-row>

      <b-form-row v-if="!readOnly && initConfig.mode === 'cluster'">
        <div v-if="newCluster" class="col-8 offset-2">
          {{$t('message.init_cluster_info')}}
        </div>
        <div v-if="!newCluster" class="col-4">
          <span class="label label-default">
            {{$t('message.init_join_cluster_node_ip') | title}}
          </span>
        </div>
        <div v-if="!newCluster" class="col-4">
          <vue-ip-input class="ip-input" :ip="initConfig.join_cluster_node_ip" :on-change="onJoinClusterNodeIpChange"/>
        </div>
      </b-form-row>

      <div v-if="initConfig.mode === 'cluster' && (readOnly || newCluster)">
        <b-form-row>
          <div class="col-4">
            <span class="label label-default">
              1. {{$t('message.init_node_ip') | title}}
            </span>
          </div>
          <div class="col-7">
            <vue-ip-input v-if="!readOnly" class="ip-input"
              :ip="initConfig.cluster_nodes[0]" :on-change="onNodeIpChange0"/>
            <input v-else v-model="initConfig.cluster_nodes[0]" disabled>
          </div>
        </b-form-row>

        <b-form-row>
          <div class="col-4">
            <span class="label label-default">
              2. {{$t('message.init_node_ip') | title}}
            </span>
          </div>
          <div class="col-7">
            <vue-ip-input v-if="!readOnly" class="ip-input" :ip="initConfig.cluster_nodes[1]"
              :on-change="onNodeIpChange1"/>
            <input v-else v-model="initConfig.cluster_nodes[1]" disabled>
          </div>
        </b-form-row>

        <b-form-row>
          <div class="col-4">
            <span class="label label-default">
              3. {{$t('message.init_node_ip') | title}}
            </span>
          </div>
          <div class="col-7">
            <vue-ip-input v-if="!readOnly" class="ip-input"
              :ip="initConfig.cluster_nodes[2]" :on-change="onNodeIpChange2"/>
            <input v-else v-model="initConfig.cluster_nodes[2]" disabled>
          </div>
        </b-form-row>
      </div>

      <b-form-row>
        <div v-if="!readOnly && initConfig.mode === 'cluster'" class="col-8 offset-2">
          {{$t("message.init_mode_new_cluster_warning")}}
        </div>
      </b-form-row>

      <b-form-row>
        <div v-if="error" class="form-error col-8 offset-2">
          {{error}}
        </div>
      </b-form-row>

      <b-button v-if="initConfig.mode == 'cluster' && initConfig.state === 'mode set'"
        type="button" class="padmin-button" @click="verifyCluster">
        {{$t('message.button_verify') | title}}
      </b-button>

      <b-button :disabled="readOnly || saveInProgress" type="button" variant="primary"
        class="padmin-button submit-button right" @click="save">
        <b-spinner v-if="saveInProgress" small type="grow"/>
        {{$t('message.button_save') | title}}
      </b-button>

      <b-button v-if="initConfig && initConfig.state == 'mode set'" type="button"
        class="padmin-button next-button right" :disabled="saveInProgress" @click="next">
        {{$t('message.button_next') | title}}
      </b-button>
    </b-form>

    <ConsoleOutput v-if="actionInitiated" :result="result" :output="output" :errorCode="errorCode"/>

    <b-modal ref="page-reload-modal" id="page-reload-modal-id" size="sm"
      ok-only :title="$t('message.page_reload_title')" @ok="pageReload" no-close-on-backdrop>
      {{$t('message.page_reload_desc')}}
    </b-modal>

  </div>
</template>

<script>
import VueIpInput from "vue-ip-input"
import { get, post } from "../http"
import ConsoleOutput from "./ConsoleOutput"
import { EventBus } from "../event-bus.js"
import {
  validateIpAddress,
  hasDuplicate,
  validateHostName
} from "../util"

export default {
  name: "InitMode",

  components: {
    ConsoleOutput,
    "vue-ip-input": VueIpInput
  },

  data() {
    return {
      initConfig: null,
      networkConfig: null,
      hostname: "",
      modeOptions: ["single", "cluster"],
      newCluster: false,
      result: null,
      errorCode: null,
      output: "",
      error: "",
      saveInProgress: false,
      actionInitiated: false
    };
  },

  mounted() {
    this.load();
  },

  computed: {
    readOnly() {
      return this.initConfig.state !== "network set";
    }
  },

  methods: {
    onNewClusterChanged() {
      this.newCluster = !this.newCluster
      if (this.newCluster && this.networkConfig) {
        this.initConfig.cluster_nodes = [this.networkConfig.static_ip]
      }
    },

    loadProgress(commandId) {
      get("/command_progress/" + commandId).then(
        resp => {
          this.output = resp.data.output
          this.result = resp.data.result
          this.saveInProgress = resp.data.in_progress
          if (this.saveInProgress || this.result == undefined) {
            setTimeout(() => {
              this.loadProgress(commandId);
            }, 1000);
          } else {
            this.errorCode = resp.data.error_code
            this.error = resp.data.error
            get("/init/mode").then(
              resp => {
                this.initConfig = resp.data
                if (this.result) {
                  if (this.initConfig['mode'] == 'cluster') {
                    if (this.initConfig['state'] == 'initialized'){
                      this.$root.$emit("bv::show::modal", "reboot-local-modal-id")
                    } else {
                      this.$root.$emit("bv::show::modal", "page-reload-modal-id")
                    }
                  }
                }
              },
              err => {
                console.log("err", err);
              }
            );
          }
        },
        err => {
          this.result = false
          this.saveInProgress = false
          this.error = err
          console.log("err", err);
        }
      );
    },

    load() {
      this.loadInitConfig()

      get("/network").then(
        resp => {
          this.networkConfig = resp.data;
        },
        err => {
          console.log("err", err)
        }
      );
    },

    loadInitConfig() {
      get("/init/mode").then(
        resp => {
          this.initConfig = resp.data;
          if (this.initConfig['cloud_mode_allowed']) {
            this.modeOptions.push('cloud')
          }
        },
        err => {
          console.log("err", err)
        }
      )
    },

    save() {
      this.result = null
      this.output = ""
      this.error = ""
      this.errorCode = null

      if (this.initConfig.mode === "cloud") {
        if (!this.hostname || this.hostname.length < 3 || !validateHostName(this.hostname)) {
          this.error = this.$t("message.error_invalid_hostname")
          return
        }
      } else if (this.initConfig.mode === "cluster" && !this.newCluster) {
        if (!validateIpAddress(this.initConfig.join_cluster_node_ip)) {
          this.error = this.$t("message.error_invalid_ip") + ": " +
            this.$t("message.init_join_cluster_node_ip") + " " + this.initConfig["join_cluster_node_ip"]
          return
        }

        if (this.newCluster) {
          for (let iNode = 0; iNode < this.initConfig.cluster_nodes.length; iNode++) {
            let nodeIp = this.initConfig.cluster_nodes[iNode];
            if (!validateIpAddress(nodeIp)) {
              this.error = this.$t("message.error_invalid_ip") + ": " +
                this.$t("message.init_node_ip") + " " + nodeIp
              return
            }
          }

          if (!this.initConfig.cluster_nodes.includes(
              this.networkConfig.static_ip)) {
            this.error = this.$t("message.error_cluster_this_ip_not_included")
            return
          }

          if (hasDuplicate(this.initConfig.cluster_nodes)) {
            this.error = this.$t("message.error_cluster_duplicate");
            return
          }
        }
      }

      this.saveInProgress = true
      this.actionInitiated = true
      let data = {
        hostname: this.hostname,
        mode: this.initConfig.mode,
      }

      if (this.newCluster) {
        data['cluster_nodes'] = this.initConfig.cluster_nodes
      }
      else {
        data['join_cluster_node_ip'] = this.initConfig.join_cluster_node_ip
      }

      post("/init/mode", data, { "Content-Type": "application/json" }).then(
        resp => {
          if (resp.data.result === false) {
            this.saveInProgress = false
            this.result = false
            if (resp.data.error) {
              this.error = this.$t('message.error_' + resp.data.error)
            }
          } else {
            // async save on backend, so check progress for response
            this.loadProgress(resp.data.command_id)
          }
        },
        err => {
          this.result = false;
          this.saveInProgress = false;
          console.log("err", err);
        }
      );
    },

    verifyCluster() {
      this.result = null
      this.output = ""
      this.error = ""
      this.actionInitiated = true
      this.errorCode = null

      get("/init/verify_cluster").then(
        resp => {
          this.result = resp.data.result;
          this.output = resp.data.output;
        },
        err => {
          console.log("err", err);
        }
      );
    },

    pageReload() {
      window.location.reload()
    },

    onNodeIpChange0(ip) {
      this.initConfig.cluster_nodes[0] = ip.replace(/\s+/g, '')
    },

    onNodeIpChange1(ip) {
      this.initConfig.cluster_nodes[1] = ip.replace(/\s+/g, '')
    },

    onNodeIpChange2(ip) {
      this.initConfig.cluster_nodes[2] = ip.replace(/\s+/g, '')
    },

    onJoinClusterNodeIpChange(ip) {
      this.initConfig.join_cluster_node_ip = ip.replace(/\s+/g, '')
    },

    next() {
      EventBus.$emit('progress init')
    }
  }
}
</script>

<style scoped>
#mode-select {
  width: 120px;
}
.form-row{
  margin:10px 0
}
.form-row input{
  width:100%;
}
</style>
